import { FloatingSearch } from "@/components/ui";
import { Messages } from "@/components/ui";
import { useDebounce } from "@/hooks/useDebounce";
import useProfile from "@/hooks/useProfile";
import {
	getGroupQueryOptions,
	markReadAllMessageQueryOptions,
} from "@/queries";
import {
	CHAT_GROUP_TABS,
	ChatGroupSearchSchema,
	PostCreateMessageBodySchema,
} from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { type QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import AppBarGroupChat from "../../-components/AppBarGroup";
import EmptyChat from "../../-components/Empty";

import { QUERY_KEYS } from "@/libraries/constant";
import { getMessagesQueryOptions } from "@/queries";
import { DataIdSchema } from "@/schemas";
import { onlineManager } from "@tanstack/react-query";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { z } from "zod";
import PendingComponent from "./-components/PendingComponent";

export const ChatGroupTabs = [
	{
		label: "Chat",
		value: CHAT_GROUP_TABS.ALL,
	},
	{
		label: "HEMI AI",
		value: CHAT_GROUP_TABS.AI,
	},
	{
		label: "Question",
		value: CHAT_GROUP_TABS.QUESTION,
	},
	{
		label: "Topic",
		value: CHAT_GROUP_TABS.TOPIC,
	},
	{
		label: "Media",
		value: CHAT_GROUP_TABS.MEDIA,
	},
	{
		label: "Published",
		value: CHAT_GROUP_TABS.PUBLISHED,
	},
];

export const Route = createFileRoute("/dashboard/chat/group/$id/$channelName")({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
			channelName: z.string().parse(params.channelName),
		}),
		stringify: ({ id, channelName }) => ({ id: id.toString(), channelName }),
	},
	beforeLoad: async ({ context, params, cause }) => {
		const queryClient: QueryClient = context.queryClient;
		if (cause === "enter") {
			const groupItem = await queryClient.fetchQuery(
				getGroupQueryOptions(params.id),
			);
			if (groupItem.group.unread && params.channelName)
				await queryClient.prefetchQuery(
					markReadAllMessageQueryOptions({
						channel_name: params.channelName,
					}),
				);
		}
	},
	loaderDeps: ({ search }) => ({ tags: search.tab }),
	loader: ({ context, params, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getMessagesQueryOptions({
					channel_name: params.channelName,
					tags: deps.tags === CHAT_GROUP_TABS.ALL ? undefined : deps.tags,
				}),
			);
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_MESSAGES,
			{
				channel_name: params.channelName,
				tags: deps.tags === CHAT_GROUP_TABS.ALL ? undefined : deps.tags,
			},
		]);
	},
	validateSearch: zodSearchValidator(ChatGroupSearchSchema),
	component: RouteComponent,
	pendingComponent: PendingComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const groupId = Route.useParams({
		select: (params) => params.id,
	});
	const channelName = Route.useParams({
		select: (params) => params.channelName,
	});
	const search = Route.useSearch({
		select: (search) => search.q || "",
	});
	const tab = Route.useSearch({
		select: (search) => search.tab,
	});
	const debounceQ = useDebounce(search, 500);
	const { profile } = useProfile();
	const queryGroup = useSuspenseQuery(getGroupQueryOptions(groupId));

	const channelMembers = useMemo(() => {
		if (!queryGroup.data?.group?.members?.length) return [];
		return queryGroup.data.group.members.filter((m) => m.id !== profile?.id);
	}, [queryGroup.data, profile]);

	const form = useForm<PostCreateMessageBodySchema>({
		mode: "all",
		resolver: zodResolver(PostCreateMessageBodySchema),
		defaultValues: async () =>
			Promise.resolve({
				channel_name: channelName,
				tags: tab && tab !== "all" ? tab : null,
			}),
	});

	const onChangeTab = (val: string) => {
		return navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				tab: val,
			}),
			replace: true,
		});
	};

	const onChangeSearch = (val: string) => {
		return navigate({
			search: (oldSearch: ChatGroupSearchSchema) => ({
				...oldSearch,
				q: val || undefined,
			}),
			replace: true,
		});
	};

	if (!profile) return null;

	return (
		<>
			<AppBarGroupChat
				type="group"
				id={groupId}
				title={queryGroup.data.group.name}
				tabs={ChatGroupTabs}
				activeTab={tab}
				onChangeTab={onChangeTab}
			/>
			<div className="relative container flex gap-1 justify-end py-3">
				<FloatingSearch
					name="search"
					value={search}
					onClear={() => onChangeSearch("")}
					onChange={(e) => onChangeSearch(e.target.value)}
				/>
			</div>
			<FormProvider {...form}>
				<Messages
					module="chats"
					channelType="group"
					channelName={channelName}
					members={channelMembers}
					search={{
						search: debounceQ,
						tab,
					}}
					renderEmpty={<EmptyChat />}
				/>
			</FormProvider>
		</>
	);
}
